// One display vocabulary for source-reported services and observed destinations.
const service = (name, aliases = []) => ({ name, aliases });
const lookup = (mapping, key) => Object.hasOwn(mapping, key) ? mapping[key] : undefined;
export const INFERENCE_SERVICE_IDENTITIES = Object.freeze({
  anthropic: service("Anthropic"), openai: service("OpenAI"),
  google: service("Google"), microsoft: service("Microsoft"),
  openrouter: service("OpenRouter"), "github-copilot": service("GitHub Copilot"),
  "aws-bedrock": service("Amazon Bedrock", ["aws.bedrock", "bedrock"]),
  "vertex-ai": service("Vertex AI", ["gcp.vertex_ai", "vertex_ai"]),
  "azure-openai": service("Azure OpenAI", ["azure.ai.openai", "azure_openai"]),
  "azure-ai-inference": service("Azure AI Inference", ["azure.ai.inference"]),
  "gemini-api": service("Gemini API", ["gcp.gemini"]),
  "google-gen-ai": service("Google generative AI (service unspecified)", ["gcp.gen_ai"]),
  xai: service("xAI", ["x_ai"]), local: service("Local inference"),
  cohere: service("Cohere"), deepseek: service("DeepSeek"), groq: service("Groq"),
  mistral: service("Mistral AI", ["mistral_ai"]), perplexity: service("Perplexity"),
  "ibm-watsonx-ai": service("IBM watsonx.ai", ["ibm.watsonx.ai"]),
  "moonshot-ai": service("Moonshot AI", ["moonshot_ai"]),
});
const aliases = new Map(Object.entries(INFERENCE_SERVICE_IDENTITIES).flatMap(([id, spec]) => [id, ...spec.aliases].map(alias => [alias, id])));
// These integration names only have these meanings in LangSmith metadata.
const LANGSMITH_ALIASES = new Map([
  ["google_vertexai", "vertex-ai"], ["vertexai", "vertex-ai"],
  ["azure", "azure-openai"], ["google_genai", "google-gen-ai"],
]);

export function reportedService(value, source = "") {
  if (typeof value !== "string" || !value.trim() || value.trim().toLowerCase() === "unknown") return null;
  const key = value.trim().toLowerCase();
  return (source === "langsmith" ? LANGSMITH_ALIASES.get(key) : null) || aliases.get(key) || value.trim();
}

export function inferenceProviderLabel(provider) {
  const id = reportedService(provider);
  return id && Object.hasOwn(INFERENCE_SERVICE_IDENTITIES, id) ? INFERENCE_SERVICE_IDENTITIES[id].name : (id || "");
}

export function destinationService(value) {
  if (typeof value !== "string" || !value || /[\s\\\u0000-\u001f\u007f]/.test(value)) return null;
  try {
    // Parse a display-only copy. The source fact, including ports and URL text, stays unchanged.
    const address = /^[\da-f:]+$/i.test(value) && value.split(":").length > 2 ? `[${value}]` : value;
    const url = new URL(address.includes("://") ? address : `https://${address}`);
    if (!["https:", "http:"].includes(url.protocol) || url.username || url.password) return null;
    const host = url.hostname.toLowerCase().replace(/\.$/, "");
    const exact = lookup({
      "api.anthropic.com": "anthropic", "api.openai.com": "openai",
      "openrouter.ai": "openrouter", "api.x.ai": "xai",
      "generativelanguage.googleapis.com": "gemini-api",
    }, host);
    if (exact) return exact;
    if (/^bedrock-runtime(?:-fips)?\.[a-z0-9-]+\.amazonaws\.com(?:\.cn)?$/.test(host)) return "aws-bedrock";
    if (/^(?:[a-z0-9-]+-)?aiplatform\.googleapis\.com$/.test(host)) return "vertex-ai";
    if (/^[a-z0-9-]+\.openai\.azure\.com$/.test(host)) return "azure-openai";
    return host ? `${host}${url.port ? `:${url.port}` : ""}` : null;
  } catch {
    return null;
  }
}
